import { format, parseISO } from 'date-fns';
import i18n from '@/lib/i18n';
import { getDateFnsLocale, getDatePattern } from '@/lib/dateLocale';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import type { CurrencyCode } from '@/stores/usePreferencesStore';
import { detectNotificationCurrency } from '@/lib/notificationParsers/utils';

// ─── Currency ────────────────────────────────────────────────

const currencyConfigs: Record<CurrencyCode, { locale: string; currency: string }> = {
    BRL: { locale: 'pt-BR', currency: 'BRL' },
    USD: { locale: 'en-US', currency: 'USD' },
    EUR: { locale: 'de-DE', currency: 'EUR' },
};

/**
 * Temporary fixed exchange rates used when a bank notification arrives in a
 * foreign currency but the app still stores transactions in a single currency.
 *
 * Values represent how many BRL one unit of the source currency is worth.
 */
export const FIXED_NOTIFICATION_EXCHANGE_RATES_TO_BRL: Record<CurrencyCode, number> = {
    BRL: 1,
    USD: 5.7,
    EUR: 6.2,
};

// Cache formatters for performance
const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(code: CurrencyCode, compact = false): Intl.NumberFormat {
    const key = `${code}_${compact}`;
    if (!formatterCache.has(key)) {
        const cfg = currencyConfigs[code];
        formatterCache.set(
            key,
            new Intl.NumberFormat(cfg.locale, {
                style: 'currency',
                currency: cfg.currency,
                ...(compact ? { notation: 'compact' } : {}),
            }),
        );
    }
    return formatterCache.get(key)!;
}

/**
 * Dia do mês pra interpolar em frases ("Vence dia {{day}}" / "Due on the {{day}}").
 * EN ganha sufixo ordinal (1st/2nd/3rd/15th); pt-BR fica o número puro — por isso
 * o texto PT não muda e usuário existente não vê diferença nenhuma.
 */
export function formatDayOfMonthOrdinal(day: number): string {
    if (!i18n.language?.startsWith('en')) return String(day);
    const mod10 = day % 10;
    const mod100 = day % 100;
    const suffix = mod100 >= 11 && mod100 <= 13 ? 'th'
        : mod10 === 1 ? 'st'
            : mod10 === 2 ? 'nd'
                : mod10 === 3 ? 'rd'
                    : 'th';
    return `${day}${suffix}`;
}

export function roundMonetaryValue(value: number): number {
    // Troca −0 por 0: um resíduo negativo (−4e-15) arredonda pra −0, que o Intl formata
    // "-R$ 0,00" — o Disponível de quem guardou tudo aparecia negativo. Não é `|| 0`: esse
    // também viraria NaN em 0 e esconderia a origem do erro.
    const rounded = Math.round(value * 100) / 100;
    return rounded === 0 ? 0 : rounded;
}

export function formatCurrency(value: number): string {
    const code = usePreferencesStore.getState().currency ?? 'BRL';
    return formatCurrencyWithCode(value, code);
}

export function formatCurrencyWithCode(value: number, code: CurrencyCode): string {
    const rounded = roundMonetaryValue(value);
    // Evita "-R$ 0,00": qualquer valor que arredonde a zero é exibido como zero
    // positivo (o -0 do JS e frações ínfimas negativas trazem o sinal indesejado).
    return getFormatter(code).format(rounded === 0 ? 0 : rounded);
}

// Formatador sem centavos, pro espaço apertado dos widgets de tela inicial
// ("R$ 2.480"). Trunca (não arredonda) pra nunca exibir um saldo maior que o real.
const noCentsFormatterCache = new Map<CurrencyCode, Intl.NumberFormat>();

export function formatCurrencyNoCents(value: number): string {
    const code = usePreferencesStore.getState().currency ?? 'BRL';
    if (!noCentsFormatterCache.has(code)) {
        const cfg = currencyConfigs[code];
        noCentsFormatterCache.set(
            code,
            new Intl.NumberFormat(cfg.locale, {
                style: 'currency',
                currency: cfg.currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }),
        );
    }
    const truncated = Math.trunc(roundMonetaryValue(value));
    return noCentsFormatterCache.get(code)!.format(truncated === 0 ? 0 : truncated);
}

// Inteiro com separadores da moeda, SEM símbolo ("12.200") — coluna compacta dos widgets.
const bareNumberFormatterCache = new Map<CurrencyCode, Intl.NumberFormat>();

export function formatNumberNoCents(value: number): string {
    const code = usePreferencesStore.getState().currency ?? 'BRL';
    if (!bareNumberFormatterCache.has(code)) {
        bareNumberFormatterCache.set(
            code,
            new Intl.NumberFormat(currencyConfigs[code].locale, { maximumFractionDigits: 0 }),
        );
    }
    const truncated = Math.trunc(roundMonetaryValue(value));
    return bareNumberFormatterCache.get(code)!.format(truncated === 0 ? 0 : truncated);
}

export function getFixedExchangeRate(from: CurrencyCode, to: CurrencyCode): number {
    const fromInBrl = FIXED_NOTIFICATION_EXCHANGE_RATES_TO_BRL[from];
    const toInBrl = FIXED_NOTIFICATION_EXCHANGE_RATES_TO_BRL[to];

    return roundMonetaryValue(fromInBrl / toInBrl);
}

export function convertFixedCurrencyAmount(value: number, from: CurrencyCode, to: CurrencyCode): number {
    if (!Number.isFinite(value)) return 0;
    if (from === to) return roundMonetaryValue(value);

    const fromInBrl = FIXED_NOTIFICATION_EXCHANGE_RATES_TO_BRL[from];
    const toInBrl = FIXED_NOTIFICATION_EXCHANGE_RATES_TO_BRL[to];

    return roundMonetaryValue((value * fromInBrl) / toInBrl);
}

export function resolveNotificationAmount(value: number, rawText?: string | null, targetCode?: CurrencyCode) {
    const sourceCurrency = detectNotificationCurrency(rawText ?? '');
    const resolvedTargetCode = targetCode ?? usePreferencesStore.getState().currency ?? 'BRL';

    return {
        sourceCurrency,
        targetCurrency: resolvedTargetCode,
        originalAmount: roundMonetaryValue(value),
        convertedAmount: convertFixedCurrencyAmount(value, sourceCurrency, resolvedTargetCode),
        exchangeRate: getFixedExchangeRate(sourceCurrency, resolvedTargetCode),
        isConverted: sourceCurrency !== resolvedTargetCode,
    };
}

export function formatNotificationAmount(value: number, rawText?: string | null): string {
    const resolved = resolveNotificationAmount(value, rawText);
    return formatCurrencyWithCode(resolved.convertedAmount, resolved.targetCurrency);
}

export function buildNotificationConversionNote(value: number, rawText?: string | null, targetCode?: CurrencyCode): string | undefined {
    const resolved = resolveNotificationAmount(value, rawText, targetCode);
    if (!resolved.isConverted) return undefined;

    return [
        `Original em ${resolved.sourceCurrency}: ${formatCurrencyWithCode(resolved.originalAmount, resolved.sourceCurrency)}.`,
        `Taxa fixa: 1 ${resolved.sourceCurrency} = ${formatCurrencyWithCode(resolved.exchangeRate, resolved.targetCurrency)}.`,
    ].join(' ');
}

export function formatCurrencyAbsolute(value: number): string {
    return formatCurrency(Math.abs(value));
}

const currencySymbols: Record<CurrencyCode, string> = {
    BRL: 'R$',
    USD: '$',
    EUR: '€',
};

/** Símbolo da moeda (ex.: 'R$', '$', '€') — pro prefixo do AmountInput. */
export function getCurrencySymbol(code: CurrencyCode): string {
    return currencySymbols[code];
}

// Formatadores só-número (sem símbolo), pro campo do AmountInput que mostra o
// símbolo à parte. Separadores seguem o locale da moeda (pt-BR '1.234,56', en-US '1,234.56').
const numberFormatterCache = new Map<CurrencyCode, Intl.NumberFormat>();

/** Valor formatado com separadores da moeda, 2 casas, SEM símbolo (ex.: '1.234,56'). */
export function formatAmountNumber(value: number, code: CurrencyCode): string {
    if (!numberFormatterCache.has(code)) {
        numberFormatterCache.set(
            code,
            new Intl.NumberFormat(currencyConfigs[code].locale, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }),
        );
    }
    const rounded = roundMonetaryValue(value);
    return numberFormatterCache.get(code)!.format(rounded === 0 ? 0 : rounded);
}

/**
 * Formato compacto pra eixos de gráfico ("R$ 6 mil", "R$ 1,2 mi").
 * Compactação manual: o Hermes ignora `notation: 'compact'` do Intl e devolvia
 * o valor cheio ("R$ 6.000"), que estourava a largura do eixo Y.
 */
export function formatCurrencyCompact(value: number): string {
    const code = usePreferencesStore.getState().currency ?? 'BRL';
    const symbol = currencySymbols[code];
    const rounded = roundMonetaryValue(value);
    const abs = Math.abs(rounded);
    const sign = rounded < 0 ? '-' : '';
    const trim = (n: number) => {
        const oneDecimal = Math.round(n * 10) / 10;
        return Number.isInteger(oneDecimal)
            ? String(oneDecimal)
            : String(oneDecimal).replace('.', ',');
    };
    if (abs >= 1_000_000) return `${sign}${symbol} ${trim(abs / 1_000_000)} mi`;
    if (abs >= 1_000) return `${sign}${symbol} ${trim(abs / 1_000)} mil`;
    return `${sign}${symbol} ${Math.round(abs)}`;
}

// ─── Date ────────────────────────────────────────────────────

export function formatDate(dateStr: string, pattern?: string): string {
    const fmt = pattern ?? usePreferencesStore.getState().dateFormat ?? 'dd/MM/yyyy';
    return format(parseISO(dateStr), fmt, { locale: getDateFnsLocale() });
}

/**
 * Hora pra EXIBIÇÃO: pt-BR usa "07h32" (padrão brasileiro); EN mantém "07:32".
 * A entrada é sempre o formato interno 'HH:mm' — armazenamento e ordenação não
 * mudam, só a apresentação.
 */
export function formatTimeDisplay(time: string): string {
    return i18n.language === 'en' ? time : time.replace(':', 'h');
}

const percentFormatterCache = new Map<string, Intl.NumberFormat>();

/**
 * Porcentagem em PONTOS (49.1 → "49,1%") no MESMO locale da moeda, pra "R$ 1.449,80" e
 * "49,1%" saírem iguais na mesma linha. `toFixed` sempre põe ponto e vazava "49.1%" em
 * pt-BR (achado no e2e, bloco 17). Não usa `style: 'percent'`: no de-DE ele insere um
 * espaço antes do sinal e o número teria que vir em razão, não em pontos.
 */
export function formatPercent(points: number, { decimals = 0, signed = false }: { decimals?: number; signed?: boolean } = {}): string {
    const code = usePreferencesStore.getState().currency ?? 'BRL';
    const key = `${code}_${decimals}`;
    let fmt = percentFormatterCache.get(key);
    if (!fmt) {
        fmt = new Intl.NumberFormat(currencyConfigs[code].locale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
        percentFormatterCache.set(key, fmt);
    }
    const rounded = Number(points.toFixed(decimals));
    const safe = rounded === 0 ? 0 : rounded; // evita "-0"
    return `${signed && safe > 0 ? '+' : ''}${fmt.format(safe)}%`;
}

/** Razão (0.491 → "49%"), inteiro. Passa pelo mesmo locale de `formatPercent`. */
export function formatPercentage(value: number): string {
    return formatPercent(value * 100);
}

export function formatMonth(dateStr: string): string {
    const date = parseISO(dateStr + (dateStr.length === 7 ? '-01' : ''));
    return format(date, getDatePattern('monthYear'), { locale: getDateFnsLocale() });
}

export function formatMonthShort(dateStr: string): string {
    const date = parseISO(dateStr + (dateStr.length === 7 ? '-01' : ''));
    return format(date, 'MMM', { locale: getDateFnsLocale() });
}

/**
 * A data é hoje ou ontem? Use isto em vez de comparar o texto de
 * `formatDateRelative` com 'Hoje'/'Ontem' — o texto agora é traduzido (ADR 0044)
 * e a comparação por string quebraria em inglês.
 */
export function isRelativeDay(dateStr: string): boolean {
    const date = parseISO(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return (
        date.toDateString() === today.toDateString()
        || date.toDateString() === yesterday.toDateString()
    );
}

export function formatDateRelative(dateStr: string): string {
    const date = parseISO(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
        return i18n.t('common:relativeDate.today');
    }
    if (date.toDateString() === yesterday.toDateString()) {
        return i18n.t('common:relativeDate.yesterday');
    }
    return format(date, getDatePattern('dayMonth'), { locale: getDateFnsLocale() });
}

/**
 * Rótulo de data com ano, para listas que misturam meses/anos (ex.: busca em
 * todo o período). Sem "Hoje/Ontem" porque ali o ano é o que desambigua.
 */
export function formatDateFull(dateStr: string): string {
    return format(parseISO(dateStr), getDatePattern('dayMonthYear'), { locale: getDateFnsLocale() });
}

/**
 * Acumulador de centavos para campo de TEXTO: a mesma digitação do AmountInput
 * (começa vazio mostrando "0,00" e vai preenchendo da direita — "8" vira 0,08,
 * "899" vira 8,99), mas devolvendo a string já formatada.
 *
 * Existe porque os campos do ajuste fino do financiamento (juros, seguros, saldo
 * devedor) não podem ser AmountInput: no desenho deles a unidade ("% a.a.", "R$")
 * vive DENTRO do campo. Sem máscara eles aceitavam "250000" e o número ficava
 * ambíguo na tela — era o "sem vírgula" que o Laysson apontou.
 *
 * Devolve '' quando não sobrou dígito: campo vazio volta ao placeholder, que é como
 * o schema distingue "não informei" de zero. O resultado é sempre relido por
 * `parseTypedNumber`, que entende o separador de milhar que o Intl coloca.
 */
export function maskTypedAmount(value: string, code: CurrencyCode): string {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    return formatAmountNumber(parseInt(digits, 10) / 100, code);
}

/**
 * Número digitado à mão → `number`. Devolve `null` quando não há número nenhum.
 *
 * O `decimal-pad` mostra ponto OU vírgula conforme o locale do APARELHO, e os campos
 * aceitam os dois — então "qual separador é o decimal" não pode ser decidido pelo
 * caractere. É decidido pelo FORMATO: o último separador é o decimal quando vem
 * seguido de 1 ou 2 dígitos; fora disso, todos são milhar.
 *
 * Cobre "1.234,56", "8.99", "1500.75" e "1.500" sem o usuário ter que saber qual tecla
 * o teclado dele mostrou. Tratar todo ponto como milhar (o que o parser de extrato faz,
 * e ali está certo, porque lá o texto vem do banco em pt-BR) lia "1500.75" como 150075:
 * cem vezes o valor, sem erro nenhum.
 */
export function parseTypedNumber(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed || !/^[\d.,]+$/.test(trimmed)) return null;

    const digitsOnly = trimmed.replace(/[.,]/g, '');
    if (!digitsOnly) return null;

    const lastSeparator = Math.max(trimmed.lastIndexOf('.'), trimmed.lastIndexOf(','));
    const decimals = lastSeparator >= 0 ? trimmed.length - lastSeparator - 1 : 0;
    const normalized = decimals >= 1 && decimals <= 2
        ? `${digitsOnly.slice(0, digitsOnly.length - decimals)}.${digitsOnly.slice(digitsOnly.length - decimals)}`
        : digitsOnly;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}
